// Silver Sage — Pending Recovery store.
// When a Chief-of-Staff-style operation fails because of a transient issue
// (network, timeout, service unavailable), we quietly remember it here so
// MAVIS can bring it back up the next morning. Never forces Savanna to
// remember unfinished work herself.
//
// Stored in localStorage under one key so it survives reloads without
// requiring a database migration. Each entry is small and self-describing:
// enough to greet with "Yesterday we weren't able to record Holly's July
// payroll…" and deep-link back to the exact record.

const KEY = "silver-sage:pending-recovery:v1";

export interface PendingRecovery {
  /** Stable id — usually the original proposal's pending_id. */
  id: string;
  /** e.g. "payroll", "registration", "instructor". Used for morning grouping. */
  category: string;
  /** Short outcome phrase — "Holly's July payroll". */
  subject: string;
  /** One-line story of what we tried to do. */
  what: string;
  /** Plain-English reason it couldn't finish. */
  reason: string;
  /** Deep link back to the exact record (or the workspace that owns it). */
  href: string;
  /** Where to jump to reopen — outcome verb ("Finish recording Holly's payment"). */
  reopenLabel: string;
  /** ms since epoch when the failure happened. */
  createdAt: number;
  /** Free-form technical string for developer logs only. Never surfaced. */
  technical?: string;
}

type Listener = (items: PendingRecovery[]) => void;
const listeners = new Set<Listener>();

function read(): PendingRecovery[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PendingRecovery[]) : [];
  } catch {
    return [];
  }
}

function write(items: PendingRecovery[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* quota exceeded — nothing we can gracefully do */
  }
  for (const fn of listeners) fn(items);
}

export function listPendingRecoveries(): PendingRecovery[] {
  return read().sort((a, b) => b.createdAt - a.createdAt);
}

export function rememberPendingRecovery(entry: PendingRecovery): void {
  const current = read().filter((x) => x.id !== entry.id);
  current.unshift(entry);
  // Keep the store small — the newest 25 are more than enough.
  write(current.slice(0, 25));
}

export function forgetPendingRecovery(id: string): void {
  const next = read().filter((x) => x.id !== id);
  write(next);
}

export function subscribePendingRecovery(fn: Listener): () => void {
  listeners.add(fn);
  // Also react to changes from other tabs.
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) fn(read());
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(fn);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}
